import { useMemo, useState } from "react";
import { useSearch as useSearchParams, RouterLink as Link } from "../../router";
import { useLiveResource } from "../../api/live";
import { apiCall } from "../../api/client";
import { customers, type CustomerPartyDTO } from "../../api/contracts/customers";
import { Tabs, TabsList, TabsTrigger } from "../../tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { ScopedSearchButton } from "../scoped-search-button";
import { Input } from "../../input";
import { Button } from "../../button";
import { formatUsd, formatRelativeTime } from "../../format";
import { objectHref } from "../../path-label";
import { DataTable, type DataTableColumn } from "../data-table";
import { QuickCreateCustomer } from "./quick-create-customer";
import { StatStrip, type StatStripItem } from "../dashboard/stat-strip";
import { IntentBadge } from "../status-badge";
import {
  CUSTOMER_STAGE_INTENT as STAGE_INTENT,
  CUSTOMER_STAGE_LABEL as STAGE_LABEL,
  CUSTOMER_BAND_INTENT as BAND_INTENT,
  CUSTOMER_BAND_LABEL as BAND_LABEL,
} from "./customer-layers";

/**
 * PortfolioView — the Customers mode home (docs/customer-module/PRD.md §3). The
 * party-rolled-up customer base on the P0 read model: a KPI command-center row
 * (Total · Active · At-risk · Open pipeline — §3.4), a Lens selector that
 * RESHAPES the visible columns (Overview/Sales/Money/Support/Marketing — one
 * ranked queue, never a re-query; §3.4), and the List view-mode as a
 * DataTable (sortable + selectable) whose rows open the `/o/<uid>` Dossier. Board/Forecast/bulk are
 * P3; this is the List slice.
 *
 * Reads the live Portfolio via `useLiveResource` on the shared `"activity"`
 * topic (PRD §8 #3 — the dedicated `"customer"` topic is a P2 refinement, so
 * v1 needs no bus-contract change). Mounted both as the mode `dashboardSlot`
 * (bare `/customers`) and the `portfolio` domain view (`?area=portfolio`).
 */

const LENSES = [
  { key: "overview", label: "Overview" },
  { key: "sales", label: "Sales" },
  { key: "money", label: "Money" },
  { key: "support", label: "Support" },
  { key: "marketing", label: "Marketing" },
] as const;
type LensKey = (typeof LENSES)[number]["key"];

/** Which columns each lens reveals (name/stage/health are always shown). */
const LENS_COLUMNS: Record<LensKey, string[]> = {
  overview: ["name", "stage", "health", "ltv", "openDeals", "lastTouch", "owner"],
  sales: ["name", "stage", "health", "openDeals", "pipeline", "owner"],
  money: ["name", "stage", "health", "ltv", "lastPayment"],
  support: ["name", "stage", "health", "tickets", "lastTouch"],
  marketing: ["name", "stage", "health", "lastTouch"],
};

const dateSort = (a: string | null, b: string | null) =>
  (a ? Date.parse(a.replace(" ", "T")) : 0) - (b ? Date.parse(b.replace(" ", "T")) : 0);

/** Built-in saved segments (§3.1) — client-side row filters over the base. */
const SEGMENTS: Array<{
  key: string;
  label: string;
  match: (p: CustomerPartyDTO) => boolean;
}> = [
  { key: "all", label: "All customers", match: () => true },
  {
    key: "at_risk",
    label: "At-risk",
    match: (p) => p.stage === "at_risk" || p.health.band === "at_risk",
  },
  { key: "active", label: "Active", match: (p) => p.stage === "active" },
  { key: "prospects", label: "Prospects", match: (p) => p.stage === "prospect" },
  { key: "enterprise", label: "Enterprise", match: (p) => p.partyKind === "organization" },
  {
    key: "expansion",
    label: "Expansion-ready",
    match: (p) => p.stage === "active" && p.openDeals > 0,
  },
];

export interface PortfolioViewProps {
  /** Vocab-ready noun (a Clients/Patients rename flows through). Defaults to Customer. */
  noun?: { singular: string; plural: string };
}

export function PortfolioView({ noun }: PortfolioViewProps = {}) {
  const plural = noun?.plural ?? "Customers";
  const [lens, setLens] = useState<LensKey>("overview");
  const [segment, setSegment] = useState("all");
  const [bulkTag, setBulkTag] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  // The view-mode is route state (`?view=`); List + Forecast render here (Board
  // is a separate server slot, Queue is ModePage's). The mode's sub-nav owns the
  // tab row — this just picks which body to render.
  const search = useSearchParams();
  const viewMode = search?.get("view") ?? "list";
  // Scoped-search model (Issue 19): the dock owns the query; this List mirrors it
  // from the URL `?q=` and filters its own rows. The header's magnifying-glass
  // button focuses the dock (pre-scoped to Customers).
  const query = search?.get("q") ?? "";
  // Drill-in from the forecast: a stage row links to `?view=list&stage=<stage>`,
  // which filters the list to that lifecycle stage (cleared via the chip).
  const stageFilter = search?.get("stage") ?? null;

  const { data, isLoading, mutate } = useLiveResource(customers.portfolio, {}, {
    topic: "activity",
    refreshInterval: 60_000,
  });
  const parties = data?.parties ?? [];

  // KPIs are workspace-level (over the whole base, not the searched subset).
  const kpis = useMemo(() => {
    const active = parties.filter((p) => p.stage === "active").length;
    const atRisk = parties.filter((p) => p.stage === "at_risk").length;
    const openPipeline = parties.reduce((sum, p) => sum + p.openDealValueMinor, 0);
    return { total: parties.length, active, atRisk, openPipeline };
  }, [parties]);

  // Forecast aggregation (§3.1) — open + weighted pipeline by lifecycle stage.
  // Pure client rollup over the same parties (no re-query; §3.4).
  const forecastRows = useMemo(() => {
    const order: CustomerPartyDTO["stage"][] = ["prospect", "active", "at_risk", "churned"];
    const slots = new Map(
      order.map((s) => [s, { stage: s, count: 0, openMinor: 0, weightedMinor: 0 }]),
    );
    for (const p of parties) {
      const slot = slots.get(p.stage);
      if (!slot) continue;
      slot.count += 1;
      slot.openMinor += p.openDealValueMinor;
      slot.weightedMinor += p.weightedPipelineMinor;
    }
    return [...slots.values()].filter((r) => r.count > 0);
  }, [parties]);

  const rows = useMemo(() => {
    const seg = SEGMENTS.find((s) => s.key === segment) ?? SEGMENTS[0]!;
    let out = parties.filter((p) => seg.match(p));
    if (stageFilter) out = out.filter((p) => p.stage === stageFilter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter((p) => p.name.toLowerCase().includes(q));
    }
    return out;
  }, [parties, query, segment, stageFilter]);

  // Command-center KPIs as the table's CAPTION band (StatStrip variant="table"
  // passed to DataTable's `summary` slot). Folding the metrics INTO the table's
  // own Card — instead of floating a standalone strip above it — makes the whole
  // surface (KPIs · filters · columns · rows) read as ONE integrated component,
  // the pattern Agents ▸ Analytics + /styleguide/queue-integrated (treatment C)
  // use. Workspace-level, so the same caption caps both the List and Forecast.
  const kpiItems: StatStripItem[] = [
    { title: `Total ${plural.toLowerCase()}`, value: String(kpis.total) },
    { title: "Active", value: String(kpis.active) },
    { title: "At-risk", value: String(kpis.atRisk) },
    { title: "Open pipeline", value: formatUsd(kpis.openPipeline / 100) },
  ];

  const visible = LENS_COLUMNS[lens];
  const show = (key: string) => !visible.includes(key);

  const columns: DataTableColumn<CustomerPartyDTO>[] = [
    {
      key: "name",
      header: plural === "Customers" ? "Customer" : noun?.singular ?? "Customer",
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (p) => <span className="text-foreground">{p.name}</span>,
    },
    {
      key: "stage",
      header: "Stage",
      sort: (a, b) => a.stage.localeCompare(b.stage),
      render: (p) => <IntentBadge intent={STAGE_INTENT[p.stage]}>{STAGE_LABEL[p.stage]}</IntentBadge>,
    },
    {
      key: "health",
      header: "Health",
      sort: (a, b) => a.health.score - b.health.score,
      defaultDesc: true,
      render: (p) => <IntentBadge intent={BAND_INTENT[p.health.band]}>{BAND_LABEL[p.health.band]}</IntentBadge>,
    },
    {
      key: "ltv",
      header: "LTV",
      sort: (a, b) => a.ltv.lifetimeMinor - b.ltv.lifetimeMinor,
      defaultDesc: true,
      hidden: show("ltv"),
      render: (p) =>
        p.ltv.lifetimeMinor > 0 ? formatUsd(p.ltv.lifetimeMinor / 100) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "openDeals",
      header: "Open deals",
      sort: (a, b) => a.openDeals - b.openDeals,
      defaultDesc: true,
      hidden: show("openDeals"),
      render: (p) => <span className="text-muted-foreground">{p.openDeals}</span>,
    },
    {
      key: "pipeline",
      header: "Weighted pipeline",
      sort: (a, b) => a.weightedPipelineMinor - b.weightedPipelineMinor,
      defaultDesc: true,
      hidden: show("pipeline"),
      render: (p) =>
        p.weightedPipelineMinor > 0
          ? formatUsd(p.weightedPipelineMinor / 100)
          : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "lastPayment",
      header: "Last payment",
      sort: (a, b) => dateSort(a.ltv.lastTxnAt, b.ltv.lastTxnAt),
      defaultDesc: true,
      hidden: show("lastPayment"),
      render: (p) => <span className="text-muted-foreground">{formatRelativeTime(p.ltv.lastTxnAt)}</span>,
    },
    {
      key: "tickets",
      header: "Open tickets",
      sortable: false,
      hidden: show("tickets"),
      // Derived 0 until Support is modelled (P5) — labelled, not faked.
      render: () => <span className="text-muted-foreground">0 (derived)</span>,
    },
    {
      key: "lastTouch",
      header: "Last touch",
      sort: (a, b) => dateSort(a.lastTouchAt, b.lastTouchAt),
      defaultDesc: true,
      hidden: show("lastTouch"),
      render: (p) => <span className="text-muted-foreground">{formatRelativeTime(p.lastTouchAt)}</span>,
    },
    {
      key: "owner",
      header: "Owner",
      sort: (a, b) => (a.ownerActorId ?? 0) - (b.ownerActorId ?? 0),
      hidden: show("owner"),
      // Owner-name resolution (an actor-label map) is a follow-up; v1 shows
      // assignment presence honestly rather than a raw id.
      render: (p) => (
        <span className="text-muted-foreground">{p.ownerActorId != null ? "Assigned" : "Unassigned"}</span>
      ),
    },
  ];

  type ForecastRow = (typeof forecastRows)[number];
  const forecastColumns: DataTableColumn<ForecastRow>[] = [
    {
      key: "stage",
      header: "Stage",
      sort: (a, b) => a.stage.localeCompare(b.stage),
      render: (r) => <span className="text-foreground">{STAGE_LABEL[r.stage]}</span>,
    },
    {
      key: "count",
      header: "Customers",
      sort: (a, b) => a.count - b.count,
      defaultDesc: true,
      render: (r) => <span className="text-muted-foreground">{r.count}</span>,
    },
    {
      key: "open",
      header: "Open value",
      sort: (a, b) => a.openMinor - b.openMinor,
      defaultDesc: true,
      render: (r) =>
        r.openMinor > 0 ? formatUsd(r.openMinor / 100) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "weighted",
      header: "Weighted pipeline",
      sort: (a, b) => a.weightedMinor - b.weightedMinor,
      defaultDesc: true,
      render: (r) =>
        r.weightedMinor > 0
          ? formatUsd(r.weightedMinor / 100)
          : <span className="text-muted-foreground">—</span>,
    },
  ];

  // The integrated filter band — segment (rows) + lens (columns) + an optional
  // stage chip — sealed BELOW the KPI caption, inside the table's own Card (the
  // `summary` slot). The lens uses the in-card `Tabs` atom (local-state panel
  // switching, conventions rule 13); the band's own `border-b` caps it against
  // the column-header row beneath.
  const filterBand = (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-2.5">
      {stageFilter ? (
        <Button variant="outline" size="sm" asChild>
          <Link href="/customers?view=list" aria-label="Clear stage filter">
            {STAGE_LABEL[stageFilter as CustomerPartyDTO["stage"]] ?? stageFilter}
            <span aria-hidden className="ml-1 text-muted-foreground">
              ✕
            </span>
          </Link>
        </Button>
      ) : null}
      <Select value={segment} onValueChange={setSegment}>
        <SelectTrigger className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SEGMENTS.map((s) => (
            <SelectItem key={s.key} value={s.key}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Tabs value={lens} onValueChange={(v) => setLens(v as LensKey)}>
        <TabsList>
          {LENSES.map((l) => (
            <TabsTrigger key={l.key} value={l.key}>
              {l.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );

  // Search + create — the per-list primary actions, riding the flush header
  // (the `headerAction` slot, beside the title + count).
  const headerActions = (
    <div className="flex items-center gap-2">
      <ScopedSearchButton label={`Search ${plural.toLowerCase()}`} />
      <QuickCreateCustomer onCreated={() => void mutate()} />
    </div>
  );

  // One integrated surface per view-mode: the KPI caption + (for the List) the
  // filter band ride the table's own Card via `summary`, the actions ride its
  // flush header — no floating strip, no detached toolbar above an unrelated
  // table (the queue-integrated thesis, applied to the Portfolio).
  return viewMode === "forecast" ? (
    <DataTable
      title="Pipeline by stage"
      count={forecastRows.length}
      columns={forecastColumns}
      rows={forecastRows}
      rowKey={(r) => r.stage}
      initialSort={{ key: "weighted", dir: "desc" }}
      rowHref={(r) => `/customers?view=list&stage=${r.stage}`}
      summary={<StatStrip items={kpiItems} variant="table" />}
      empty={isLoading ? "Loading…" : "No pipeline to forecast yet."}
    />
  ) : (
    <DataTable
      title={plural}
      count={rows.length}
      headerAction={headerActions}
      summary={
        <>
          <StatStrip items={kpiItems} variant="table" />
          {filterBand}
        </>
      }
      selectable
      columns={columns}
      rows={rows}
      rowKey={(p) => p.uid}
      initialSort={{ key: "name", dir: "asc" }}
      rowHref={(p) => objectHref(p.uid, "customers")}
      keyboardNav
      pageSize={25}
      empty={
        isLoading
          ? "Loading…"
          : query
            ? `No ${plural.toLowerCase()} match “${query}”.`
            : `No ${plural.toLowerCase()} yet — leads, deals, and payments roll up here as they land.`
      }
      renderBulkBar={(selectedParties, clear) => (
        <div className="flex items-center gap-2">
          <Input
            value={bulkTag}
            onChange={(e) => setBulkTag(e.target.value)}
            placeholder="Tag name…"
            className="h-7 w-44"
          />
          <Button
            size="sm"
            disabled={!bulkTag.trim() || bulkBusy}
            onClick={async () => {
              setBulkBusy(true);
              try {
                await apiCall(customers.bulk, {
                  body: {
                    ids: selectedParties.map((p) => p.uid),
                    action: "add_tag",
                    args: { tag: bulkTag.trim() },
                  },
                });
                setBulkTag("");
                clear();
                await mutate();
              } finally {
                setBulkBusy(false);
              }
            }}
          >
            Add tag
          </Button>
        </div>
      )}
    />
  );
}
